import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Upload, FileText, MessageSquare, Clock, Receipt,
  FileSignature, StickyNote, Download, UserCog, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import SecretWarning from "@/components/SecretWarning";
import { LoadingBlock, EmptyState, Card, formatDate, formatDateTime, formatCurrency } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { useI18n, useL } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { walletName, problemLabel, getDocCategories, docCategoryLabel } from "@/data/site";

const CASE_STATUS_CODES = Object.keys(STATUS_LABELS);
const SENSITIVE = ["REJECTED", "UNSUCCESSFUL", "CANCELLED", "CLOSED"];
const REASONS = {
  "Pratica inviata": ["Pratica inviata", "Case submitted", "Fall eingereicht"],
  "Convertito da lead": ["Convertito da lead", "Converted from lead", "Aus Anfrage umgewandelt"],
  "Creata dall'amministratore": ["Creata dall'amministratore", "Created by administrator", "Vom Administrator erstellt"],
  "Preventivo creato": ["Preventivo creato", "Quote created", "Angebot erstellt"],
  "Preventivo accepted": ["Preventivo accettato", "Quote accepted", "Angebot angenommen"],
  "Preventivo rejected": ["Preventivo rifiutato", "Quote rejected", "Angebot abgelehnt"],
  "Contratto accettato": ["Contratto accettato", "Contract accepted", "Vertrag angenommen"],
};

export default function CaseDetail({ basePath = "/portal/cases" }) {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const L = useL();
  const isStaff = ["specialist", "admin", "super_admin"].includes(user?.role);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const [caseData, setCaseData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [internalMsg, setInternalMsg] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, tl, m, d, dr, q, ct, inv] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/timeline`),
        api.get(`/cases/${id}/messages`),
        api.get(`/cases/${id}/documents`),
        api.get(`/cases/${id}/document-requests`),
        api.get(`/cases/${id}/quotes`),
        api.get(`/cases/${id}/contracts`),
        api.get(`/cases/${id}/invoices`),
      ]);
      setCaseData(c.data); setTimeline(tl.data); setMessages(m.data);
      setDocuments(d.data); setDocRequests(dr.data); setQuotes(q.data);
      setContracts(ct.data); setInvoices(inv.data);
      if (isStaff) {
        api.get(`/cases/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
      }
      if (isAdmin) {
        api.get("/admin/specialists").then((r) => setSpecialists(r.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id, isStaff, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = async () => {
    if (!msg.trim()) return;
    try {
      await api.post(`/cases/${id}/messages`, { body: msg, internal: internalMsg });
      setMsg("");
      const { data } = await api.get(`/cases/${id}/messages`);
      setMessages(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadDoc = async (file, category) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/cases/${id}/documents`, {
          filename: file.name, category, content_base64: reader.result, mime_type: file.type,
        });
        toast.success(L("Documento caricato", "Document uploaded", "Dokument hochgeladen"));
        const { data } = await api.get(`/cases/${id}/documents`);
        setDocuments(data);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const { data } = await api.get(`/documents/${docId}/download`);
      const a = document.createElement("a");
      a.href = data.content_base64.startsWith("data:") ? data.content_base64 : `data:${data.mime_type || "application/octet-stream"};base64,${data.content_base64}`;
      a.download = filename;
      a.click();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const quoteDecision = async (quoteId, decision) => {
    try {
      await api.post(`/quotes/${quoteId}/decision`, { decision });
      toast.success(decision === "accepted" ? L("Preventivo accettato", "Quote accepted", "Angebot angenommen") : L("Preventivo rifiutato", "Quote rejected", "Angebot abgelehnt"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const acceptContract = async (cid) => {
    try { await api.post(`/contracts/${cid}/accept`); toast.success(L("Contratto accettato", "Contract accepted", "Vertrag angenommen")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <LoadingBlock />;
  if (!caseData) return <EmptyState icon={AlertCircle} title={L("Pratica non trovata", "Case not found", "Fall nicht gefunden")} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to={basePath} className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{L("Torna alle pratiche", "Back to cases", "Zurück zu den Fällen")}</Link>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-2xl font-medium text-white font-mono">{caseData.reference}</h1>
            <StatusBadge status={caseData.status} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="overview">
            <TabsList className="bg-navy-paper border border-white/10 flex-wrap h-auto">
              <TabsTrigger value="overview" data-testid="tab-overview"><FileText className="h-4 w-4 mr-1.5" />{L("Panoramica", "Overview", "Übersicht")}</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline"><Clock className="h-4 w-4 mr-1.5" />{L("Timeline", "Timeline", "Verlauf")}</TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare className="h-4 w-4 mr-1.5" />{L("Messaggi", "Messages", "Nachrichten")}</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents"><Upload className="h-4 w-4 mr-1.5" />{L("Documenti", "Documents", "Dokumente")}</TabsTrigger>
              <TabsTrigger value="billing" data-testid="tab-billing"><Receipt className="h-4 w-4 mr-1.5" />{L("Preventivi & Fatture", "Quotes & Invoices", "Angebote & Rechnungen")}</TabsTrigger>
              {isStaff && <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="h-4 w-4 mr-1.5" />{L("Note interne", "Internal notes", "Interne Notizen")}</TabsTrigger>}
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="mt-4">
              <Card className="p-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <Field label={L("Tipo wallet", "Wallet type", "Wallet-Typ")} value={walletName(caseData.wallet_type)} />
                  <Field label={L("Problema", "Problem", "Problem")} value={problemLabel(caseData.problem_type, lang)} />
                  <Field label={L("Priorità", "Priority", "Priorität")} value={caseData.priority || L("normale", "normal", "normal")} />
                  <Field label={L("Criptovaluta", "Cryptocurrency", "Kryptowährung")} value={caseData.cryptocurrency || "—"} />
                  <Field label={L("Anno creazione", "Creation year", "Erstellungsjahr")} value={caseData.creation_year || "—"} />
                  <Field label={L("Aperta il", "Opened on", "Eröffnet am")} value={formatDate(caseData.created_at)} />
                  <Field label={L("Specialista", "Specialist", "Spezialist")} value={caseData.assigned_specialist_name || L("Non assegnato", "Unassigned", "Nicht zugewiesen")} />
                  <Field label={L("Sistema", "System", "System")} value={caseData.operating_system || "—"} />
                  <Field label={L("Valore (autodich.)", "Value (self-declared)", "Wert (Selbstauskunft)")} value={caseData.approx_value || "—"} />
                </div>
                {caseData.public_address && <Field label={L("Indirizzo pubblico", "Public address", "Öffentliche Adresse")} value={<span className="font-mono text-xs break-all">{caseData.public_address}</span>} />}
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">{L("Descrizione", "Description", "Beschreibung")}</p>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{caseData.description}</p>
                </div>
                {caseData.available_material?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">{L("Materiale disponibile", "Available material", "Verfügbare Unterlagen")}</p>
                    <div className="flex flex-wrap gap-2">
                      {caseData.available_material.map((m) => <span key={m} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-300">{m}</span>)}
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* TIMELINE */}
            <TabsContent value="timeline" className="mt-4">
              <Card className="p-6">
                {timeline.length === 0 ? <EmptyState icon={Clock} title={L("Nessun evento", "No events", "Keine Ereignisse")} /> : (
                  <ol className="relative border-l border-white/10 ml-2" data-testid="case-timeline">
                    {timeline.map((t) => (
                      <li key={t.id} className="mb-6 ml-6">
                        <span className="absolute -left-[7px] h-3.5 w-3.5 rounded-full bg-cyanx border-2 border-navy" />
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge status={t.to_status} />
                          <span className="text-xs text-slate-500">{formatDateTime(t.created_at)}</span>
                        </div>
                        {t.reason && <p className="mt-1.5 text-sm text-slate-400">{REASONS[t.reason] ? L(...REASONS[t.reason]) : t.reason}</p>}
                        {t.actor_email && <p className="text-xs text-slate-600 mt-0.5">{L("da", "by", "von")} {t.actor_email}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </Card>
            </TabsContent>

            {/* MESSAGES */}
            <TabsContent value="messages" className="mt-4">
              <Card className="p-6">
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="messages-list">
                  {messages.length === 0 ? <EmptyState icon={MessageSquare} title={L("Nessun messaggio", "No messages", "Keine Nachrichten")} description={L("Avvia la conversazione qui sotto.", "Start the conversation below.", "Starten Sie die Konversation unten.")} /> :
                    messages.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${m.internal ? "bg-gold/10 border border-gold/30" : mine ? "bg-cyanx/15 border border-cyanx/20" : "bg-white/5 border border-white/10"}`}>
                            {m.internal && <span className="text-[10px] uppercase tracking-wider text-gold">{L("Nota interna", "Internal note", "Interne Notiz")}</span>}
                            <p className="text-sm text-slate-200 whitespace-pre-line">{m.body}</p>
                            <p className="text-[10px] text-slate-500 mt-1">{m.sender_email || (mine ? L("Tu", "You", "Sie") : L("Team Recovers Crypto", "Recovers Crypto team", "Recovers-Crypto-Team"))} · {formatDateTime(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <div className="mt-4 border-t border-white/10 pt-4 space-y-2">
                  <SecretWarning />
                  <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder={L("Scrivi un messaggio…", "Write a message…", "Nachricht schreiben…")} className="bg-navy border-white/10" data-testid="message-input" />
                  <div className="flex items-center justify-between">
                    {isStaff && (
                      <label className="flex items-center gap-2 text-xs text-slate-400">
                        <input type="checkbox" checked={internalMsg} onChange={(e) => setInternalMsg(e.target.checked)} data-testid="internal-toggle" />
                        {L("Nota interna (non visibile al cliente)", "Internal note (not visible to client)", "Interne Notiz (für Kunden nicht sichtbar)")}
                      </label>
                    )}
                    <Button onClick={sendMessage} className="ml-auto bg-cyanx text-navy hover:bg-cyanx-hover" data-testid="send-message-btn"><Send className="h-4 w-4 mr-1.5" />{L("Invia", "Send", "Senden")}</Button>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* DOCUMENTS */}
            <TabsContent value="documents" className="mt-4 space-y-4">
              {docRequests.length > 0 && (
                <Card className="p-5">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{L("Documenti richiesti", "Requested documents", "Angeforderte Dokumente")}</p>
                  <div className="space-y-2">
                    {docRequests.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-lg border border-gold/20 bg-gold/5 px-4 py-2.5 text-sm">
                        <span className="text-slate-200">{docCategoryLabel(r.category, lang)}{r.note ? ` — ${r.note}` : ""}</span>
                        <span className="text-xs text-gold">{r.status}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500">{L("Documenti", "Documents", "Dokumente")} ({documents.length})</p>
                  <UploadDialog onUpload={uploadDoc} />
                </div>
                {documents.length === 0 ? <EmptyState icon={FileText} title={L("Nessun documento", "No documents", "Keine Dokumente")} description={L("Carica i documenti richiesti in modo sicuro.", "Upload the requested documents securely.", "Laden Sie die angeforderten Dokumente sicher hoch.")} /> : (
                  <div className="space-y-2" data-testid="documents-list">
                    {documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{d.filename}</p>
                          <p className="text-xs text-slate-500">{docCategoryLabel(d.category, lang)} · {(d.size / 1024).toFixed(0)} KB · <span className={d.review_status === "approved" ? "text-emerald-400" : d.review_status === "rejected" ? "text-red-400" : "text-slate-400"}>{d.review_status}</span></p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isStaff && (
                            <select defaultValue={d.review_status} onChange={async (e) => { await api.patch(`/documents/${d.id}/review`, { review_status: e.target.value }); toast.success(L("Revisione aggiornata", "Review updated", "Prüfung aktualisiert")); load(); }} className="h-8 rounded-md bg-navy-paper border border-white/10 text-xs px-2 text-white" data-testid={`doc-review-${d.id}`}>
                              <option value="pending">{L("In attesa", "Pending", "Ausstehend")}</option><option value="approved">{L("Approvato", "Approved", "Genehmigt")}</option><option value="rejected">{L("Rifiutato", "Rejected", "Abgelehnt")}</option>
                            </select>
                          )}
                          <Button size="sm" variant="outline" className="border-white/20 h-8" onClick={() => downloadDoc(d.id, d.filename)} data-testid={`doc-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* BILLING */}
            <TabsContent value="billing" className="mt-4 space-y-4">
              <Card className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{L("Preventivi", "Quotes", "Angebote")}</p>
                {quotes.length === 0 ? <p className="text-sm text-slate-500">{L("Nessun preventivo.", "No quotes.", "Keine Angebote.")}</p> : (
                  <div className="space-y-3" data-testid="quotes-list">
                    {quotes.map((q) => (
                      <div key={q.id} className="rounded-lg border border-white/10 bg-navy p-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <p className="text-sm text-white">{q.fee_model === "fixed" ? formatCurrency(q.amount, q.currency) : q.fee_model === "percentage" ? `${q.percentage}%` : L("Struttura personalizzata", "Custom structure", "Individuelle Struktur")}</p>
                            <p className="text-xs text-slate-500">{L("Validità", "Validity", "Gültigkeit")}: {formatDate(q.validity_date)} · {L("Stato", "Status", "Status")}: {q.status}</p>
                          </div>
                          {!isStaff && q.status === "sent" && (
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-8" onClick={() => quoteDecision(q.id, "accepted")} data-testid={`quote-accept-${q.id}`}>{L("Accetta", "Accept", "Annehmen")}</Button>
                              <Button size="sm" variant="outline" className="border-white/20 h-8" onClick={() => quoteDecision(q.id, "rejected")} data-testid={`quote-reject-${q.id}`}>{L("Rifiuta", "Reject", "Ablehnen")}</Button>
                            </div>
                          )}
                        </div>
                        {q.scope && <p className="mt-2 text-xs text-slate-400">{L("Ambito", "Scope", "Umfang")}: {q.scope}</p>}
                        {q.client_notes && <p className="mt-1 text-xs text-slate-400">{L("Note", "Notes", "Notizen")}: {q.client_notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <Card className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{L("Contratti", "Contracts", "Verträge")}</p>
                {contracts.length === 0 ? <p className="text-sm text-slate-500">{L("Nessun contratto.", "No contracts.", "Keine Verträge.")}</p> : (
                  <div className="space-y-3" data-testid="contracts-list">
                    {contracts.map((c) => (
                      <div key={c.id} className="rounded-lg border border-white/10 bg-navy p-4 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-sm text-white font-mono">{c.number} · v{c.version}</p>
                          <p className="text-xs text-slate-500">{c.scope} · {L("Stato", "Status", "Status")}: {c.status}</p>
                        </div>
                        {!isStaff && c.status === "pending" && (
                          <Button size="sm" className="bg-gold text-navy hover:bg-gold-hover h-8" onClick={() => acceptContract(c.id)} data-testid={`contract-accept-${c.id}`}>{L("Accetta contratto", "Accept contract", "Vertrag annehmen")}</Button>
                        )}
                        {c.status === "accepted" && <span className="text-xs text-emerald-400">{L("Accettato il", "Accepted on", "Angenommen am")} {formatDate(c.accepted_at)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <Card className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{L("Fatture e pagamenti", "Invoices and payments", "Rechnungen und Zahlungen")}</p>
                {invoices.length === 0 ? <p className="text-sm text-slate-500">{L("Nessuna fattura.", "No invoices.", "Keine Rechnungen.")}</p> : (
                  <div className="space-y-3" data-testid="invoices-list">
                    {invoices.map((inv) => (
                      <div key={inv.id} className="rounded-lg border border-white/10 bg-navy p-4 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-sm text-white font-mono">{inv.number}</p>
                          <p className="text-xs text-slate-500">{formatCurrency(inv.amount, inv.currency)} · {L("Scadenza", "Due", "Fällig")}: {formatDate(inv.due_date)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${inv.status === "PAID" ? "text-emerald-400" : "text-slate-400"}`}>{inv.status}</span>
                          {isAdmin && <PaymentDialog invoice={inv} onDone={load} />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {isStaff && (
              <TabsContent value="notes" className="mt-4">
                <NotesPanel caseId={id} notes={notes} onAdded={() => api.get(`/cases/${id}/notes`).then((r) => setNotes(r.data))} />
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          {isStaff && <StaffActions caseData={caseData} isAdmin={isAdmin} specialists={specialists} onDone={load} />}
          {!isStaff && (
            <Card className="p-5">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{L("Prossima azione", "Next action", "Nächster Schritt")}</p>
              <p className="text-sm text-slate-300">{nextAction(caseData.status, L)}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200">{value}</p>
    </div>
  );
}

function nextAction(status, L) {
  const map = {
    SUBMITTED: L("La pratica è in attesa di revisione da parte del team.", "The case is awaiting review by the team.", "Der Fall wartet auf die Prüfung durch das Team."),
    IDENTITY_VERIFICATION_REQUIRED: L("Carica un documento d'identità nella sezione Documenti.", "Upload an identity document in the Documents section.", "Laden Sie im Bereich Dokumente einen Ausweis hoch."),
    OWNERSHIP_VERIFICATION: L("Fornisci una prova di titolarità del wallet.", "Provide proof of wallet ownership.", "Legen Sie einen Eigentumsnachweis für das Wallet vor."),
    DOCUMENTS_REQUESTED: L("Carica i documenti richiesti.", "Upload the requested documents.", "Laden Sie die angeforderten Dokumente hoch."),
    QUOTE_AVAILABLE: L("Rivedi e accetta il preventivo nella sezione Preventivi & Fatture.", "Review and accept the quote in the Quotes & Invoices section.", "Prüfen und akzeptieren Sie das Angebot im Bereich Angebote & Rechnungen."),
    CONTRACT_PENDING: L("Accetta il contratto per procedere.", "Accept the contract to proceed.", "Akzeptieren Sie den Vertrag, um fortzufahren."),
    ADDITIONAL_INFORMATION_REQUIRED: L("Fornisci le informazioni aggiuntive richieste.", "Provide the additional information requested.", "Stellen Sie die angeforderten Zusatzinformationen bereit."),
  };
  return map[status] || L("Nessuna azione richiesta al momento.", "No action required at the moment.", "Derzeit ist keine Aktion erforderlich.");
}

function UploadDialog({ onUpload }) {
  const L = useL();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("identity_document");
  const [file, setFile] = useState(null);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-cyanx text-navy hover:bg-cyanx-hover" data-testid="upload-doc-btn"><Upload className="h-4 w-4 mr-1.5" />{L("Carica", "Upload", "Hochladen")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Carica documento", "Upload document", "Dokument hochladen")}</DialogTitle></DialogHeader>
        <SecretWarning />
        <div className="space-y-3">
          <div>
            <Label className="text-slate-300">{L("Categoria", "Category", "Kategorie")}</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="upload-category">
              {getDocCategories(lang).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-slate-300">{L("File (PDF, immagini, doc — max 10MB)", "File (PDF, images, doc — max 10MB)", "Datei (PDF, Bilder, Doc — max. 10 MB)")}</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 bg-navy border-white/10" data-testid="upload-file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt,.csv" />
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!file} onClick={() => { onUpload(file, category); setOpen(false); setFile(null); }} className="bg-gold text-navy hover:bg-gold-hover" data-testid="upload-confirm">{L("Carica", "Upload", "Hochladen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ invoice, onDone }) {
  const L = useL();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(invoice.status);
  const [ref, setRef] = useState("");
  const save = async () => {
    try { await api.patch(`/invoices/${invoice.id}/payment`, { status, provider_reference: ref, paid_date: status === "PAID" ? new Date().toISOString() : null }); toast.success(L("Pagamento registrato", "Payment recorded", "Zahlung erfasst")); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="border-white/20 h-8" data-testid={`payment-btn-${invoice.id}`}>{L("Registra pagamento", "Record payment", "Zahlung erfassen")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Registra pagamento", "Record payment", "Zahlung erfassen")} — {invoice.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-300">{L("Stato", "Status", "Status")}</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="payment-status">
              {["ISSUED", "PENDING", "PAID", "FAILED", "REFUNDED", "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label className="text-slate-300">{L("Riferimento provider", "Provider reference", "Anbieter-Referenz")}</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} className="mt-1.5 bg-navy border-white/10" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-gold text-navy hover:bg-gold-hover" data-testid="payment-save">{L("Salva", "Save", "Speichern")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotesPanel({ caseId, notes, onAdded }) {
  const L = useL();
  const [body, setBody] = useState("");
  const add = async () => {
    if (!body.trim()) return;
    try { await api.post(`/cases/${caseId}/notes`, { body }); setBody(""); onAdded(); toast.success(L("Nota aggiunta", "Note added", "Notiz hinzugefügt")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-6">
      <div className="space-y-2 max-h-72 overflow-y-auto mb-4" data-testid="notes-list">
        {notes.length === 0 ? <p className="text-sm text-slate-500">{L("Nessuna nota interna.", "No internal notes.", "Keine internen Notizen.")}</p> :
          notes.map((n) => (
            <div key={n.id} className="rounded-lg border border-gold/20 bg-gold/5 p-3">
              <p className="text-sm text-slate-200">{n.body}</p>
              <p className="text-[10px] text-slate-500 mt-1">{n.author} · {formatDateTime(n.created_at)}</p>
            </div>
          ))}
      </div>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder={L("Nota interna…", "Internal note…", "Interne Notiz…")} className="bg-navy border-white/10" data-testid="note-input" />
      <Button onClick={add} className="mt-2 bg-gold text-navy hover:bg-gold-hover" data-testid="add-note-btn">{L("Aggiungi nota", "Add note", "Notiz hinzufügen")}</Button>
    </Card>
  );
}

function StaffActions({ caseData, isAdmin, specialists, onDone }) {
  const L = useL();
  const [status, setStatus] = useState(caseData.status);
  const [reason, setReason] = useState("");
  const needReason = SENSITIVE.includes(status);

  const changeStatus = async () => {
    try { await api.patch(`/cases/${caseData.id}/status`, { status, reason }); toast.success(L("Stato aggiornato", "Status updated", "Status aktualisiert")); setReason(""); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const assign = async (sid) => {
    try { await api.post(`/cases/${caseData.id}/assign`, { specialist_id: sid }); toast.success(L("Specialista assegnato", "Specialist assigned", "Spezialist zugewiesen")); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setPriority = async (p) => {
    try { await api.patch(`/cases/${caseData.id}/priority`, { priority: p }); toast.success(L("Priorità aggiornata", "Priority updated", "Priorität aktualisiert")); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <Card className="p-5 space-y-3">
        <p className="text-xs uppercase tracking-wider text-slate-500">{L("Aggiorna stato", "Update status", "Status aktualisieren")}</p>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="status-select">
          {CASE_STATUS_CODES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        {needReason && <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder={L("Motivazione (obbligatoria)", "Reason (required)", "Begründung (erforderlich)")} className="bg-navy border-white/10" data-testid="status-reason" />}
        <Button onClick={changeStatus} className="w-full bg-cyanx text-navy hover:bg-cyanx-hover" data-testid="update-status-btn">{L("Aggiorna stato", "Update status", "Status aktualisieren")}</Button>
      </Card>

      {isAdmin && (
        <>
          <Card className="p-5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" />{L("Assegna specialista", "Assign specialist", "Spezialist zuweisen")}</p>
            <select defaultValue={caseData.assigned_specialist_id || ""} onChange={(e) => e.target.value && assign(e.target.value)} className="w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="assign-select">
              <option value="">{L("Seleziona specialista…", "Select specialist…", "Spezialist auswählen…")}</option>
              {specialists.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.active_cases} {L("attive", "active", "aktiv")})</option>)}
            </select>
            <div>
              <p className="text-xs text-slate-500 mb-1.5">{L("Priorità", "Priority", "Priorität")}</p>
              <div className="grid grid-cols-4 gap-1.5">
                {["low", "normal", "high", "urgent"].map((p) => (
                  <button key={p} onClick={() => setPriority(p)} className={`rounded-md border px-2 py-1.5 text-xs ${caseData.priority === p ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:bg-white/5"}`} data-testid={`priority-${p}`}>{p}</button>
                ))}
              </div>
            </div>
          </Card>
          <BillingActions caseId={caseData.id} onDone={onDone} />
        </>
      )}
      <RequestDocAction caseId={caseData.id} onDone={onDone} />
    </>
  );
}

function RequestDocAction({ caseId, onDone }) {
  const L = useL();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("identity_document");
  const [note, setNote] = useState("");
  const submit = async () => {
    try { await api.post(`/cases/${caseId}/document-requests`, { category, note }); toast.success(L("Documento richiesto", "Document requested", "Dokument angefordert")); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full border-white/20 text-white" data-testid="request-doc-btn"><FileText className="h-4 w-4 mr-1.5" />{L("Richiedi documento", "Request document", "Dokument anfordern")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Richiedi documento", "Request document", "Dokument anfordern")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="request-category">
            {getDocCategories(lang).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={L("Nota (facoltativa)", "Note (optional)", "Notiz (optional)")} className="bg-navy border-white/10" />
        </div>
        <DialogFooter><Button onClick={submit} className="bg-gold text-navy hover:bg-gold-hover" data-testid="request-doc-confirm">{L("Richiedi", "Request", "Anfordern")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillingActions({ caseId, onDone }) {
  const L = useL();
  return (
    <Card className="p-5 space-y-2">
      <p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FileSignature className="h-3.5 w-3.5" />{L("Fatturazione", "Billing", "Abrechnung")}</p>
      <QuoteDialog caseId={caseId} onDone={onDone} />
      <ContractDialog caseId={caseId} onDone={onDone} />
      <InvoiceDialog caseId={caseId} onDone={onDone} />
    </Card>
  );
}

function QuoteDialog({ caseId, onDone }) {
  const L = useL();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ fee_model: "fixed", currency: "EUR", amount: "", percentage: "", validity_date: "", scope: "", client_notes: "" });
  const submit = async () => {
    try {
      await api.post("/quotes", { case_id: caseId, ...f, amount: f.amount ? parseFloat(f.amount) : null, percentage: f.percentage ? parseFloat(f.percentage) : null });
      toast.success(L("Preventivo creato", "Quote created", "Angebot erstellt")); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full border-white/20 text-white justify-start" data-testid="create-quote-btn">+ {L("Crea preventivo", "Create quote", "Angebot erstellen")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Crea preventivo", "Create quote", "Angebot erstellen")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select value={f.fee_model} onChange={(e) => setF({ ...f, fee_model: e.target.value })} className="w-full h-10 rounded-md bg-navy border border-white/10 px-3 text-sm text-white" data-testid="quote-model">
            <option value="fixed">{L("Fisso", "Fixed", "Fest")}</option><option value="percentage">{L("Percentuale", "Percentage", "Prozentual")}</option><option value="custom">{L("Personalizzato", "Custom", "Individuell")}</option>
          </select>
          {f.fee_model === "fixed" && <Input type="number" placeholder={L("Importo", "Amount", "Betrag")} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-navy border-white/10" data-testid="quote-amount" />}
          {f.fee_model === "percentage" && <Input type="number" placeholder={L("Percentuale %", "Percentage %", "Prozentsatz %")} value={f.percentage} onChange={(e) => setF({ ...f, percentage: e.target.value })} className="bg-navy border-white/10" data-testid="quote-percentage" />}
          <Input type="date" value={f.validity_date} onChange={(e) => setF({ ...f, validity_date: e.target.value })} className="bg-navy border-white/10" data-testid="quote-validity" />
          <Textarea placeholder={L("Ambito", "Scope", "Umfang")} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} rows={2} className="bg-navy border-white/10" />
          <Textarea placeholder={L("Note per il cliente", "Notes for the client", "Notizen für den Kunden")} value={f.client_notes} onChange={(e) => setF({ ...f, client_notes: e.target.value })} rows={2} className="bg-navy border-white/10" />
        </div>
        <DialogFooter><Button onClick={submit} className="bg-gold text-navy hover:bg-gold-hover" data-testid="quote-save">{L("Invia preventivo", "Send quote", "Angebot senden")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractDialog({ caseId, onDone }) {
  const L = useL();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ scope: "", security_terms: "", fee_terms: "", version: "1.0" });
  const submit = async () => {
    try { await api.post("/contracts", { case_id: caseId, ...f }); toast.success(L("Contratto creato", "Contract created", "Vertrag erstellt")); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full border-white/20 text-white justify-start" data-testid="create-contract-btn">+ {L("Crea contratto", "Create contract", "Vertrag erstellen")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Crea contratto", "Create contract", "Vertrag erstellen")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Textarea placeholder={L("Ambito del lavoro", "Scope of work", "Arbeitsumfang")} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} rows={2} className="bg-navy border-white/10" data-testid="contract-scope" />
          <Textarea placeholder={L("Condizioni di sicurezza", "Security terms", "Sicherheitsbedingungen")} value={f.security_terms} onChange={(e) => setF({ ...f, security_terms: e.target.value })} rows={2} className="bg-navy border-white/10" />
          <Textarea placeholder={L("Termini economici", "Fee terms", "Vergütungsbedingungen")} value={f.fee_terms} onChange={(e) => setF({ ...f, fee_terms: e.target.value })} rows={2} className="bg-navy border-white/10" />
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.scope} className="bg-gold text-navy hover:bg-gold-hover" data-testid="contract-save">{L("Crea", "Create", "Erstellen")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({ caseId, onDone }) {
  const L = useL();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", currency: "EUR", payment_method: "bonifico", due_date: "" });
  const submit = async () => {
    try { await api.post("/invoices", { case_id: caseId, ...f, amount: parseFloat(f.amount) }); toast.success(L("Fattura creata", "Invoice created", "Rechnung erstellt")); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full border-white/20 text-white justify-start" data-testid="create-invoice-btn">+ {L("Crea fattura", "Create invoice", "Rechnung erstellen")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Crea fattura", "Create invoice", "Rechnung erstellen")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input type="number" placeholder={L("Importo", "Amount", "Betrag")} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-navy border-white/10" data-testid="invoice-amount" />
          <Input placeholder={L("Metodo di pagamento", "Payment method", "Zahlungsmethode")} value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className="bg-navy border-white/10" />
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="bg-navy border-white/10" data-testid="invoice-due" />
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.amount} className="bg-gold text-navy hover:bg-gold-hover" data-testid="invoice-save">{L("Crea", "Create", "Erstellen")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
